/**
 * Staff sign-in from ET Console (founder decision 2026-10-09: Console is the
 * master login for staff).
 *
 * Console's "Open AnooreHR" sends the browser to /?console_login=<code>. This
 * hook exchanges the one-time code at /auth/console-login (the backend redeems
 * it with Console through the gateway and sets this app's normal cookies),
 * strips the code from the URL so history/referrers never carry it, and
 * reloads so the session bootstraps from /auth/me.
 *
 * No-op when ?console_login is absent. Mirrors useImpersonationHandoff.
 */
import { useEffect, useState } from 'react';
import apiClient from '@/api/client';

type Status = 'idle' | 'exchanging' | 'done' | 'error';

// One request per code: the code is single-use, and React StrictMode runs
// effects twice in development -- a second POST would be refused and win.
const exchanges = new Map<string, Promise<unknown>>();

export function useConsoleLoginHandoff(): { status: Status; error: string | null } {
  // Read the code during the FIRST render: child effects (the route guard's
  // redirect to /login) run before this hook's effect and would otherwise
  // navigate the code away first. While exchanging, App renders a spinner
  // instead of the routes, so nothing redirects.
  const [code] = useState(() => new URLSearchParams(window.location.search).get('console_login'));
  const [status, setStatus] = useState<Status>(code ? 'exchanging' : 'idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;

    // Remove the code first: it is single-use, and must not survive in
    // history even if the exchange fails.
    const params = new URLSearchParams(window.location.search);
    params.delete('console_login');
    const newSearch = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));

    let cancelled = false;
    let exchange = exchanges.get(code);
    if (!exchange) {
      exchange = apiClient.post('/auth/console-login', { code });
      exchanges.set(code, exchange);
    }
    exchange
      .then(() => {
        if (cancelled) return;
        setStatus('done');
        window.location.replace('/');
      })
      .catch((e: { response?: { data?: { detail?: string } } }) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail ?? 'Signing in from Console failed. Open the app from Console again.');
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  return { status, error };
}
